import { cva, VariantProps } from 'class-variance-authority';
import React, { ReactNode } from 'react';

import { cn, toTitleCase } from '@/shared/helpers';

import { badgeVariants } from './core/badge';

const statusBadgeVariants = cva('border-transparent', {
  variants: {
    type: {
      success: 'bg-state-success text-state-success-foreground',
      progress: 'bg-state-progress text-state-progress-foreground',
      observe: 'bg-state-observe text-state-observe-foreground',
      alert: 'bg-state-alert text-state-alert-foreground',
      inactive: 'bg-state-inactive text-state-inactive-foreground',
    },
  },
});

export type StatusType = VariantProps<typeof statusBadgeVariants>['type'];
export type StatusBadgeSize = VariantProps<typeof badgeVariants>['size'];

export type StatusBadgeProps = Omit<React.HTMLAttributes<'span'>, 'title'> & {
  /** The direct status value. This will be used to index `type` and `title` if mapping objects are provided to them. */
  status?: string;

  /** This can be a status type directly, or an object which maps the `status` value to a status type. */
  type?: StatusType | Record<string, StatusType>;

  /** This can be the title to display directly, or an object which maps the `status` value to a title. */
  title?: string | Record<string, string>;

  size?: StatusBadgeSize;
};

export function StatusBadge({
  status,
  type: providedType,
  title,
  size,
  className,
  children,
}: StatusBadgeProps) {
  let type: StatusType = 'inactive';
  if (typeof providedType === 'string') {
    type = providedType;
  } else if (providedType && status) {
    type = providedType[status];
  }

  let content: ReactNode;
  if (React.Children.count(children)) {
    content = children;
  } else if (typeof title === 'string') {
    content = title;
  } else if (title && status) {
    content = title[status];
  } else if (status) {
    content = toTitleCase(status.replace('_', ' '));
  }

  return (
    <span
      className={cn(
        badgeVariants({ size, variant: 'outline' }),
        statusBadgeVariants({ type }),
        className,
      )}
    >
      {content}
    </span>
  );
}
